import { ENDPOINTS } from '../constants/endpoints'
import type { AxiosResponse } from 'axios'
import type { Donation } from '@/features/donations/types/donation'
import axiosInstance from '@/integrations/axios/axios-instance'

export interface SendAnnualReportRequest {
  memberId: string
  year: number
}

export interface SendAnnualReportResponse {
  success: boolean
  message: string
  memberId: string
  receiptNumber: string
  recipient: {
    name: string
    email: string
  }
}

export interface SendReceiptEmailRequest {
  donationId: string
}

export interface SendReceiptEmailResponse {
  message: string
  donationId: string
  sentToMember: true
  sentToMemberAt: string
  sentToMemberBy: string
}

export interface SendBulkAnnualReportsRequest {
  year: number
  churchId?: string
}

export interface SendBulkAnnualReportsResponse {
  success: boolean
  emailsSent: number
  totalDonors: number
  message: string
  recipients: Array<{
    donorId: string
    name: string
    email: string
    receiptNumber: string
  }>
  errors?: Array<string>
}

export interface SendBulkReceiptsResponse {
  success: boolean
  emailsSent: number
  totalDonations: number
  message: string
  recipients: Array<{
    donationId: string
    name: string
    email: string
    receiptCode: string
  }>
  errors?: Array<string>
  updatedDonations: Array<Donation>
}

export const receiptsApi = {
  getPDF: (wrapper: string): Promise<AxiosResponse<Blob>> => {
    const endpoint = ENDPOINTS.GET_PDF
    return axiosInstance.post(
      endpoint,
      { html: wrapper },
      { responseType: 'blob' },
    )
  },
  getPDFImage: (wrapper: string): Promise<AxiosResponse<Blob>> => {
    const endpoint = ENDPOINTS.GET_PDF_IMAGE
    return axiosInstance.post(
      endpoint,
      { html: wrapper },
      { responseType: 'blob' },
    )
  },
  getAnnualReceiptNumber: (
    donorId: string,
    year: number,
  ): Promise<AxiosResponse<{ receiptCode: string }>> => {
    const endpoint = ENDPOINTS.GET_ANNUAL_RECEIPT_NUMBER
    return axiosInstance.get(endpoint, { params: { donorId, year } })
  },

  sendReceiptEmail: (
    donationId: string,
  ): Promise<AxiosResponse<SendReceiptEmailResponse>> => {
    const endpoint = ENDPOINTS.SEND_RECEIPT
    return axiosInstance.post(endpoint, { donationId })
  },

  sendAnnualReport: (
    params: SendAnnualReportRequest,
  ): Promise<AxiosResponse<SendAnnualReportResponse>> => {
    const endpoint = ENDPOINTS.SEND_ANNUAL_REPORT
    return axiosInstance.post(endpoint, params)
  },

  sendBulkAnnualReports: (
    params: SendBulkAnnualReportsRequest,
  ): Promise<AxiosResponse<SendBulkAnnualReportsResponse>> => {
    const endpoint = ENDPOINTS.SEND_BULK_ANNUAL_REPORTS
    return axiosInstance.post(endpoint, params)
  },

  sendBulkReceipts: (): Promise<AxiosResponse<SendBulkReceiptsResponse>> => {
    const endpoint = ENDPOINTS.SEND_BULK_RECEIPTS
    return axiosInstance.post(endpoint)
  },
  downloadReceipt: (
    donationId: string,
  ): Promise<AxiosResponse<SendReceiptEmailResponse>> => {
    const endpoint = ENDPOINTS.DOWNLOAD_RECEIPT
    return axiosInstance.post(endpoint, { donationId })
  },
}
